import { Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { Router } from '@angular/router';
import { faEllipsis } from '@fortawesome/free-solid-svg-icons';
import { NgxUiLoaderService } from 'ngx-ui-loader';
import { catchError, concatMap, delay, finalize, from, Observable, take, throwError } from 'rxjs';
import { AuthService } from 'src/app/auth/auth.service';
import { DefaultRoutes, FeatureRoutes } from 'src/app/core/routes';
import { TareaReporte } from 'src/app/features/tasks/tarea-reporte';
import { Roles } from 'src/app/features/user/user';
import { ModalConfig } from 'src/app/shared/components/modal/modal-config';
import { GlobalService } from 'src/app/shared/services/global.service';
import { ModalService } from 'src/app/shared/services/modal.service';
import { REPORT_MESSAGES } from 'src/app/util/constantes-mensajes';
import { v4 as uuidv4 } from 'uuid';
import { Reporte } from '../models/reporte';
import { ReportsService } from '../reports.service';
@Component({
    selector: 'app-report-item',
    templateUrl: './report-item.component.html',
    styleUrls: ['./report-item.component.scss'],
    standalone: false
})
export class ReportItemComponent {

    // Report data that user select
    @Input() report: Reporte;
    // Property to emit an event when user removes a favorite report
    @Output() readonly removeFavorite = new EventEmitter();
    @Output() readonly onDelete = new EventEmitter();
    @Output() readonly handleDisableEnable = new EventEmitter();
    //Config to set modal properties
    public modalConfig: ModalConfig;
    //Variable to show loader
    isLoading = false;
    hasPermission = false;
    faEllipsis = faEllipsis;

    public globalService = inject(GlobalService);

    constructor(private reportService: ReportsService, private modalService: ModalService, private authService: AuthService, private router: Router, private ngxUiLoaderService: NgxUiLoaderService) {
        const rol = this.authService.user?.rol;
        this.hasPermission = rol === Roles.admin || rol === Roles.specialist;
    }

    /**
     * A function that open the modal component or generate the report task
     * @returns {void}
     */
    loadData(): void {
        this.isLoading = true;
        this.ngxUiLoaderService.start('report-item-' + this.report.uuid);
        this.reportService
            .getReportParameters(this.report.uuid)
            .pipe(
                take(1),
                delay(500),
                finalize(() => { this.isLoading = false; this.ngxUiLoaderService.stop('report-item-' + this.report.uuid); }),
                concatMap((parameters) => {
                    this.report.parametros = parameters;
                    return parameters.length > 0 ? this.openModal() : this.generateTask();
                })
            )
            .subscribe({
                next: (response) => {
                    if (response) {
                        const message = response.message || REPORT_MESSAGES.GENERATE.REPORT;
                        const confirmTitle = response.confirmTitle || "Aceptar"
                        this.modalService.getConfirmacionModal({ message, confirmTitle, isError: response.isError }).result.then(() => {
                            this.router.navigateByUrl(DefaultRoutes.tasks)
                        })
                    }
                },
                error: (error) => this.modalService.getConfirmacionModal({ message: error.error.message, confirmTitle: "Cerrar", isError: true }),
            });
    }

    get hasOpenModals(): boolean {
        return this.modalService.hasOpenModals();
    }

    /**
     * Validates the favorite property in the selected report and emits an event
     * @param {boolean} value
     */
    onFavorite(value: boolean): void {
        this.reportService.setFavorite(this.report.uuid).subscribe({
            next: () => {
                this.report.favorito = value;
                if (!this.report.favorito) {
                    this.removeFavorite.emit();
                }
            },
            error: () => this.modalService.getConfirmacionModal({ message: REPORT_MESSAGES.UPDATE.ERROR_FAVORITE, confirmTitle: "Cerrar", isError: true }),
        });
    }

    /**
    * A function that open the report parameters modal
    * @returns {Observable<any>}
    */
    // skipcq: JS-0323
    openModal(): Observable<any> {
        const modal = this.modalService.getReportParametersModal(this.report).result.then();
        return from(modal).pipe(delay(500));
    }

    /**
    * A function that creates the task report
    * @returns {Observable<any>}
    */
    // skipcq: JS-0323
    generateTask(): Observable<any> {
        const tareaReporte: TareaReporte = {
            uuid: uuidv4(),
            reporte: this.report.uuid,
            usuario: this.authService?.user?.uuid ?? '',
            parametros: []
        };
        return this.reportService.generateReportTask(tareaReporte).pipe(
            catchError((error) => throwError(() => error))
        );
    }

    /**
    * A function that redirect to edit page.
    * @returns None
    */
    editReport() {
        this.router.navigateByUrl(`${DefaultRoutes.dataVisualization}${FeatureRoutes.reports}/edit-report/${this.report.uuid}`);
    }

    /**
    * A function that call delete report service
    * @returns None
    */
    deleteReport() {
        this.reportService.deleteReport(this.report.uuid).subscribe({
            next: (res) => {
                this.onDelete.emit(this.report.uuid);
                this.modalService.getConfirmacionModal({
                    message: res.message,
                    confirmTitle: "Cerrar",
                    isError: false
                });

            },
            error: () => {
                this.modalService.getConfirmacionModal({
                    message: REPORT_MESSAGES.DELETE.ERROR,
                    confirmTitle: "Cerrar",
                    isError: true
                });
            },
        });
    }

    /**
    * A function that call disable report service
    * @returns None
    */
    disableReport() {
        this.reportService.disableReport(this.report.uuid).subscribe({
            next: () => {
                this.handleDisableEnable.emit(this.report.uuid);
                this.modalService.getConfirmacionModal({
                    message: "Proceso realizado correctamente.",
                    confirmTitle: "Cerrar",
                    isError: false
                });

            },
            error: () => {
                this.modalService.getConfirmacionModal({
                    message: REPORT_MESSAGES.DELETE.ERROR,
                    confirmTitle: "Cerrar",
                    isError: true
                });
            },
        });
    }
}